import { useEffect, useState } from 'react';
import axios from 'axios';
import { CreditCard, RefreshCw, ShieldCheck, Calendar, AlertTriangle, Mail, HelpCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  active:    { label: 'Active',     className: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  trialing:  { label: 'Trialing',   className: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  past_due:  { label: 'Past Due',   className: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  cancelled: { label: 'Cancelled',  className: 'bg-rose-500/15 text-rose-300 border-rose-500/30' },
  default:   { label: 'Inactive',   className: 'bg-slate-500/15 text-slate-300 border-slate-500/30' }
};

const CURRENCY_SYMBOL = { gbp: '£', eur: '€', usd: '$' };

export default function BillingPlan() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    axios.get(`${API}/me/billing`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  if (loading) {
    return (
      <div className="p-8 text-slate-400">Loading billing details…</div>
    );
  }

  const pc = data?.plan_config || {};
  const override = !!data?.manual_override_enabled;
  const statusKey = (data?.billing_status || 'default').toLowerCase();
  const status = STATUS_STYLE[statusKey] || STATUS_STYLE.default;
  const currencySymbol = CURRENCY_SYMBOL[(data?.billing_currency || '').toLowerCase()] || '';
  const planLabel = pc.plan_type ? pc.plan_type.charAt(0).toUpperCase() + pc.plan_type.slice(1) : (override ? 'Custom' : 'No plan');

  return (
    <div className="p-8 space-y-6" data-testid="billing-plan-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Billing & Plan</h1>
        <p className="text-sm text-slate-500 mt-1">Manage your subscription, billing status and plan limits.</p>
      </div>

      {override && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-violet-50 border border-violet-200" data-testid="billing-manual-override-note">
          <ShieldCheck className="w-5 h-5 text-violet-600 shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-violet-900">Managed manually by Super Admin</div>
            <p className="text-xs text-violet-700 mt-0.5">
              Your dashboard access is controlled by your account team and is not affected by Stripe billing status.
            </p>
          </div>
        </div>
      )}

      {!override && statusKey === 'past_due' && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-amber-900">Payment past due</div>
            <p className="text-xs text-amber-700 mt-0.5">We could not charge your card. Please update your payment method to keep dashboard access.</p>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-6 soft-shadow">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Current Plan</h2>
              <p className="text-xs text-slate-500">Your dashboard subscription</p>
            </div>
          </div>
          <div className="space-y-3">
            <Row label="Plan" value={planLabel} big />
            <Row label="Billing" value={data?.billing_interval ? `${titleCase(data.billing_interval)} (${(data.billing_currency || '').toUpperCase()})` : '-'} />
            <Row label="Status" value={
              <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold border ${status.className}`} data-testid="billing-status-pill">
                {status.label}
              </span>
            } />
            <Row label="Renews" value={data?.current_period_end ? new Date(data.current_period_end).toLocaleDateString(undefined, { year:'numeric', month:'long', day:'numeric' }) : '-'} />
            <Row label="Last payment" value={data?.last_payment_at ? new Date(data.last_payment_at).toLocaleDateString() : '-'} />
          </div>
          <div className="flex gap-2 mt-6">
            <button
              onClick={() => window.location.href = '/plan-selection'}
              className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-sm font-semibold hover:shadow-lg hover:shadow-violet-600/30 transition-all flex items-center justify-center gap-2"
              data-testid="billing-change-plan"
            >
              <RefreshCw className="w-4 h-4" /> Change Plan
            </button>
          </div>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-6 soft-shadow">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Plan Limits</h2>
              <p className="text-xs text-slate-500">Your usage allowances this period</p>
            </div>
          </div>
          <div className="space-y-3">
            <Row label="Keywords tracked" value={pc.keywords_limit ?? 0} />
            <Row label="Blogs / month" value={pc.blogs_limit ?? 0} />
            <Row label="Backlinks target" value={pc.backlinks_limit ?? 0} />
            <Row label="AI Visibility" value={pc.ai_visibility_enabled ? 'Enabled' : '-'} />
            <Row label="Account Manager" value={pc.account_manager_enabled ? 'Enabled' : '-'} />
            <Row label="Team chat" value={pc.chat_enabled ? 'Enabled' : '-'} />
          </div>
        </div>
      </div>

      {/* Downgrade / Cancellation - support-only flow.
          Per company policy clients cannot self-service cancel or downgrade
          from inside the dashboard. They must email support so the team can
          confirm the change while honouring the rest of the paid cycle. */}
      <div
        className="rounded-2xl border border-slate-200 bg-white/70 backdrop-blur-sm p-6 soft-shadow"
        data-testid="billing-cancel-downgrade-block"
      >
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center shrink-0">
            <HelpCircle className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Need to downgrade or cancel?</h2>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed">
              To downgrade or cancel your subscription, please contact our support team. Your access continues until the end of your paid billing cycle. No refunds are issued for already-paid subscription periods.
            </p>
            <p className="text-xs text-slate-500 mt-2">
              Email: <span className="font-semibold text-slate-700">support@lawagency360.com</span>
            </p>
            <a
              href="mailto:support@lawagency360.com?subject=Cancellation%20or%20downgrade%20request&body=Hi%20Law%20Agency%20360%20team%2C%0A%0AI%20would%20like%20to%20%5Bcancel%2Fdowngrade%5D%20my%20subscription.%0A%0AAccount%20email%3A%20%0AReason%3A%20%0A%0AThanks!"
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-rose-500 to-pink-500 text-white text-sm font-semibold shadow-lg shadow-rose-500/30 hover:shadow-rose-500/50 transition-all"
              data-testid="billing-email-support-btn"
            >
              <Mail className="w-4 h-4" />
              Email Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function titleCase(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }

function Row({ label, value, big = false }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-600">{label}</span>
      <span className={`${big ? 'text-lg font-bold' : 'text-sm font-semibold'} text-slate-900`} style={big ? { fontFamily: 'Outfit' } : undefined}>{value}</span>
    </div>
  );
}
